import { z } from "zod";
import { IndexFolderSchema } from "../../../../skills/leadcue-reply/scripts/lib/index-folders.ts";

/*
 * The integration migration's frozen central contracts (docs/2026-10-18-integration-worker-plan.md,
 * A01). Browser-safe: the job panel reads these too, so nothing here imports a server capability.
 * Server-side ports live in `ports.ts`.
 */

export const IntegrationOwnerSchema = z.enum(["embedded", "integrations", "draining"]);
export type IntegrationOwner = z.infer<typeof IntegrationOwnerSchema>;

/**
 * Every job kind the migration allocates, with the card that executes it. Migration 118 admits the
 * same list. A build reads only the kinds it knows, so a row a newer build wrote is skipped rather
 * than failing a whole list (`KNOWN_INTEGRATION_JOB_KINDS`).
 */
export const IntegrationJobKindSchema = z.enum([
  /** Mailbox inventory: the rep's button and the hourly sync (C01 producer, C03 pages). */
  "sender_sync",
  /** One campaign's provider reads, or a workspace's copy maintenance with no resource (J01, C05). */
  "campaign_refresh",
  /** One event type's bounded page slice (foundation executor, C01 producer). */
  "reply_poll",
  /** A workspace's campaign listing, binding what Bison gained (C01). */
  "campaign_discovery",
  /** One campaign's checkpointed history catch-up (C02). */
  "reply_backfill",
  /** Re-import of open or pre-format-2 threads (C05, every 15 minutes). */
  "thread_refresh",
  /** The daily reply sweep (C05). */
  "reply_reconciliation",
  /** Claimed intake events and qualification releases, worked to a decision (C05). */
  "intake_drain",
  /** One folder's message index slice: the sub-inbox router's read (LEA-311). */
  "message_index",
]);
export type IntegrationJobKind = z.infer<typeof IntegrationJobKindSchema>;
export const KNOWN_INTEGRATION_JOB_KINDS: readonly IntegrationJobKind[] =
  IntegrationJobKindSchema.options;

const REPLY_EVENT_TYPES = ["lead_replied", "untracked_reply_received"] as const;
const CampaignBindingIdSchema = z.string().min(1).max(200);
const NoResourceSchema = z.literal("");
/**
 * The per-minute `thread_refresh` job that reads `manual_email_sent` events and refreshes the
 * conversations they name (LEA-310, R3). The open-thread sweep stays resource `""`.
 */
export const MANUAL_SENDS_RESOURCE = "manual_sends";

/**
 * What `resource_id` names for each kind. The active-job uniqueness key is
 * (workspace, kind, resource), so this decides which presses and ticks coalesce.
 */
const IntegrationJobResourceSchemas = {
  sender_sync: NoResourceSchema,
  campaign_refresh: z.union([NoResourceSchema, CampaignBindingIdSchema]),
  reply_poll: z.enum(REPLY_EVENT_TYPES),
  campaign_discovery: NoResourceSchema,
  reply_backfill: CampaignBindingIdSchema,
  thread_refresh: z.union([NoResourceSchema, z.literal(MANUAL_SENDS_RESOURCE)]),
  reply_reconciliation: NoResourceSchema,
  intake_drain: NoResourceSchema,
  message_index: IndexFolderSchema,
} as const satisfies Record<IntegrationJobKind, z.ZodType<string>>;

export function parseIntegrationJobResource(kind: IntegrationJobKind, resourceId: string): string {
  return IntegrationJobResourceSchemas[kind].parse(resourceId);
}

const IntegrationJobStateSchema = z.enum([
  "queued",
  "running",
  "retrying",
  "succeeded",
  "failed",
  "cancelled",
]);
export type IntegrationJobState = z.infer<typeof IntegrationJobStateSchema>;

export const IntegrationJobSchema = z.strictObject({
  id: z.uuid(),
  kind: IntegrationJobKindSchema,
  resourceId: z.string(),
  state: IntegrationJobStateSchema,
  attempts: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  failureCode: z.string().nullable(),
});
export type IntegrationJob = z.infer<typeof IntegrationJobSchema>;

/** Why an inventory traversal discarded its checkpoint and started again (LEA-290). */
export const InventoryRestartReason = z.enum([
  "expired",
  "lifecycle_changed",
  "publication_refused",
]);
export type InventoryRestartReason = z.infer<typeof InventoryRestartReason>;

/**
 * How far the workspace's active mailbox re-sync has got, for the person who pressed it.
 *
 * - `none`: no active `sender_sync` job.
 * - `routine`: only the scheduled upkeep runs (no press joined, no checkpoint): no bar to show.
 * - `accounts`: the account listing; `done` pages of `total`, Bison's `last_page`, unknown until
 *   its first page answers. `accountsTotal` is Bison's `meta.total`, a count of mailboxes.
 * - `campaigns`: one step per enabled campaign, fixed when the generation began; `done` counts
 *   campaigns, not pages, because a campaign's listing can take several pages.
 * - `published`: this job's traversal has published and its runner has yet to mark it finished,
 *   so the bar ends rather than stepping back to an empty account phase.
 *
 * `waiting` is `provider` when admission deferred the job (a 429 cooldown or a held key) or holds
 * one of its reads, and `retry` when a read failed and the job waits for its next attempt.
 * `restartedBecause` is set only when the traversal started again during this job, so a backward
 * step always carries its reason.
 */
export const SenderSyncProgressSchema = z.strictObject({
  jobId: z.uuid().nullable(),
  state: IntegrationJobSchema.shape.state.nullable(),
  requested: z.boolean(),
  phase: z.enum(["none", "routine", "accounts", "campaigns", "published"]),
  done: z.number().int().nonnegative(),
  total: z.number().int().nonnegative().nullable(),
  accountsTotal: z.number().int().nonnegative().nullable(),
  waiting: z.enum(["provider", "retry"]).nullable(),
  restartedBecause: InventoryRestartReason.nullable(),
  startedAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type SenderSyncProgress = z.infer<typeof SenderSyncProgressSchema>;
export const IntegrationJobResponseSchema = z.strictObject({ job: IntegrationJobSchema });
export const IntegrationJobsResponseSchema = z.strictObject({
  jobs: z.array(IntegrationJobSchema),
});
export function integrationJobActive(job: IntegrationJob): boolean {
  return job.state === "queued" || job.state === "running" || job.state === "retrying";
}

/**
 * The only payload a Temporal workflow or activity carries: opaque, scoped references. Strict, so
 * provider content, credentials or downloaded bytes added to it fail at the boundary.
 */
export const IntegrationJobReferenceSchema = z.strictObject({
  organizationId: z.string().min(1),
  workspaceId: z.string().min(1),
  jobId: z.uuid(),
  generation: z.number().int().positive(),
});
export type IntegrationJobReference = z.infer<typeof IntegrationJobReferenceSchema>;

/** What a Temporal activity returns: the job's state, nothing from the provider. */
export const IntegrationActivityResultSchema = z.strictObject({
  state: IntegrationJobStateSchema,
});

/**
 * The one read-retry policy (E02). A read is tried at most `attempts` times inside its request,
 * with backoff capped at `maximumBackoffMs`; a job whose reads failed `failedJobAfter` times in a row
 * fails; only an exhausted transient failure is produced again after `recoveryMs`. Definitive and
 * uncertain failures require explicit recovery. No other layer retries a provider
 * read, so the attempts never multiply.
 */
export const READ_RETRY_POLICY = {
  attempts: 3,
  maximumBackoffMs: 30_000,
  failedJobAfter: 3,
  recoveryMs: 5 * 60 * 1000,
} as const;

const FailureCodeSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);

/**
 * How one read job's attempt ended. The distinctions decide retry:
 * - `continued`: more pages remain; requeue now without spending the failure budget.
 * - `retryable`: transient; retry later, bounded by consecutive failures.
 * - `failed`: definitive; never retried by the runner.
 * - `uncertain`: an external effect may have happened; never retried, reconciled instead.
 * - `cancelled`: ownership, lifecycle or the caller ended it; nothing is published.
 * - `deferred`: the provider asked the key to wait; due again after `delayMs`, no failure spent.
 * Codes are ours; a provider's message never becomes a code.
 */
export const IntegrationOutcomeSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("succeeded") }),
  z.strictObject({ kind: z.literal("continued") }),
  z.strictObject({
    kind: z.literal("deferred"),
    delayMs: z
      .number()
      .int()
      .nonnegative()
      .max(24 * 60 * 60 * 1000),
  }),
  z.strictObject({ kind: z.literal("retryable"), code: FailureCodeSchema }),
  z.strictObject({ kind: z.literal("failed"), code: FailureCodeSchema }),
  z.strictObject({ kind: z.literal("uncertain"), code: FailureCodeSchema }),
  z.strictObject({ kind: z.literal("cancelled"), code: FailureCodeSchema }),
]);
export type IntegrationOutcome = z.infer<typeof IntegrationOutcomeSchema>;

const OUTCOME_UNKNOWN_CODE = "outcome_unknown";

/** Only `retryable` may be attempted again automatically. */
export function outcomeRetryable(outcome: IntegrationOutcome): boolean {
  return outcome.kind === "retryable";
}

/**
 * The persisted state for an outcome. `uncertain` is terminal `failed` with its own code, so the job
 * enum (and every rollback build reading it) is unchanged, while the reason stays visible.
 */
export function outcomeJobState(
  outcome: IntegrationOutcome,
  consecutiveFailures: number,
  maximumFailures: number,
): { state: IntegrationJobState; failureCode: string | null; terminal: boolean } {
  switch (outcome.kind) {
    case "succeeded":
      return { state: "succeeded", failureCode: null, terminal: true };
    case "continued":
    case "deferred":
      return { state: "queued", failureCode: null, terminal: false };
    case "retryable":
      return consecutiveFailures + 1 >= maximumFailures
        ? { state: "failed", failureCode: outcome.code, terminal: true }
        : { state: "retrying", failureCode: outcome.code, terminal: false };
    case "failed":
      return { state: "failed", failureCode: outcome.code, terminal: true };
    case "uncertain":
      return {
        state: "failed",
        failureCode: `${OUTCOME_UNKNOWN_CODE}_${outcome.code}`.slice(0, 64),
        terminal: true,
      };
    case "cancelled":
      return { state: "cancelled", failureCode: outcome.code, terminal: true };
  }
}

/**
 * How an external effect attempt ended (J02/J03). `refused` proves nothing started; `unknown` means
 * a request may have reached the provider, so it is never repeated automatically; only `sent` with a
 * provider receipt proves completion.
 */
export const EffectOutcomeSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("sent"), receipt: z.string().min(1).max(200) }),
  z.strictObject({ kind: z.literal("refused"), code: FailureCodeSchema }),
  z.strictObject({ kind: z.literal("unknown"), code: FailureCodeSchema }),
]);
export type EffectOutcome = z.infer<typeof EffectOutcomeSchema>;

/** Whether a new, explicitly requested attempt cannot duplicate this one. Never a runner retry. */
export function effectSafeToAttemptAgain(outcome: EffectOutcome): boolean {
  return outcome.kind === "refused";
}

/**
 * What a read's caller may keep beside the provider's raw bytes (R02, R04): how the attempt went,
 * never what it returned. Retries and rate counters stay here and never become campaign context.
 */
export const AcquisitionEnvelopeSchema = z.strictObject({
  attemptId: z.uuid(),
  /** `endpointTemplate`'s form: record ids and keys replaced, no query. */
  endpoint: z.string().min(1).max(200),
  status: z.number().int().min(100).max(599),
  dispatchedAt: z.iso.datetime(),
  rate: z
    .strictObject({
      limit: z.number().int().positive().nullable(),
      remaining: z.number().int().nonnegative().nullable(),
    })
    .nullable(),
});
export type AcquisitionEnvelope = z.infer<typeof AcquisitionEnvelopeSchema>;

/** The one origin Email Bison traffic may use; the integration store and its read fetcher both need it. */
export const EMAIL_BISON_BASE_URL = "https://bison.chaptersagency.com";
export type { BisonFetcher as Fetcher } from "../../../../skills/leadcue-reply/scripts/lib/bison-transport.ts";
